import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import ReactDOM from 'react-dom/client';
import r2wc from '@r2wc/react-to-web-component';
import Button from '@molecules/Button';
import Loader from '@view/components/Loader';
import { Title } from '@view/components/Title';
import { AccommodationDatePicker, BookNowButton, GlobalConfig, ImmersiveExperience, MultiPropertyDatePicker, PropertyDatePicker, } from './components';
import { defineGlobalFunctions } from './global-functions';
import { withChakraTheme, withShadowRootStyles } from './hoc';
import { WebComponentsTestPage } from './WebComponentsTestPage';
const createWebComponent = (name, Component, options) => {
    // Idempotent: re-defining a custom element throws and aborts registration.
    if (customElements.get(name))
        return;
    customElements.define(name, r2wc(Component, options));
};
const createThemedWebComponent = (name, Component, options) => createWebComponent(name, withChakraTheme(Component), options);
const createWebComponentWithShadowRoot = (name, Component, options) => {
    if (customElements.get(name))
        return;
    customElements.define(name, r2wc(withShadowRootStyles(Component, name), options));
};
createThemedWebComponent('cb-loader', Loader);
// r2wc maps string HTML attributes, so the precise BEButton props union (which
// requires an accessible name via the type system) doesn't apply at this
// web-component boundary; cast to a plain component type for registration.
createThemedWebComponent('cb-button', Button, {
    props: [
        'align',
        'aria-label',
        'children',
        'className',
        'color',
        'disabled',
        'icon',
        'isDisabled',
        'isLoading',
        'label',
        'onClick',
        'size',
        'testid',
        'variant',
        'weight',
        // Common Chakra UI button props
        'type',
        'form',
        'height',
        'width',
        'minWidth',
        'maxWidth',
        'padding',
        'paddingX',
        'paddingY',
        'margin',
        'marginX',
        'marginY',
        'borderRadius',
        'border',
        'borderColor',
        'backgroundColor',
        'bgColor',
        'fontSize',
        'fontWeight',
        'textAlign',
        'display',
        'position',
        'top',
        'left',
        'right',
        'bottom',
        'zIndex',
    ],
});
createWebComponent('cb-immersive-experience', ImmersiveExperience, {
    props: {
        currency: 'string',
        disableCssTitleReset: 'boolean',
        environment: 'string',
        hideCustomFooter: 'boolean',
        hideCustomHeader: 'boolean',
        hidePropertyInfo: 'boolean',
        ignoreSearchParams: 'boolean',
        island: 'string',
        lang: 'string',
        mode: 'string',
        propertyCode: 'string',
    },
});
createWebComponent('cb-book-now-button', BookNowButton, {
    props: {
        className: 'string',
        closeLabel: 'string',
        currency: 'string',
        disableCssTitleReset: 'boolean',
        environment: 'string',
        height: 'string',
        hideCustomFooter: 'boolean',
        hideCustomHeader: 'boolean',
        hidePropertyInfo: 'boolean',
        ignoreSearchParams: 'boolean',
        island: 'string',
        label: 'string',
        lang: 'string',
        popupId: 'string',
        propertyCode: 'string',
        width: 'string',
    },
});
createWebComponent('cb-global-config', GlobalConfig, {
    props: {
        lang: 'string',
        primaryColor: 'string',
    },
});
createThemedWebComponent('cb-accommodation-date-picker', AccommodationDatePicker, {
    props: {
        buttonLabel: 'string',
        className: 'string',
        currency: 'string',
        customUrl: 'string',
        island: 'string',
        lang: 'string',
        propertyCode: 'string',
        rid: 'string',
        showLowestRate: 'boolean',
        testId: 'string',
    },
});
createThemedWebComponent('cb-property-date-picker', PropertyDatePicker, {
    props: {
        buttonLabel: 'string',
        className: 'string',
        currency: 'string',
        customUrl: 'string',
        island: 'string',
        lang: 'string',
        layout: 'string',
        openInNewTab: 'string',
        propertyCode: 'string',
        testId: 'string',
    },
});
createThemedWebComponent('cb-multi-property-date-picker', MultiPropertyDatePicker, {
    props: {
        buttonLabel: 'string',
        className: 'string',
        currency: 'string',
        island: 'string',
        lang: 'string',
        layout: 'string',
        openInNewTab: 'string',
        propertyOrder: 'string',
        subDomain: 'string',
        testId: 'string',
    },
});
// This is an example of how to create a web component with shadow root, once we're able to use it for the Booking Engine, we'll deprecate the existing cb-immersive-experience webcomponent in favor of this one.
createWebComponentWithShadowRoot('cb-immersive-experience-root', () => {
    React.useEffect(() => {
        window.dispatchEvent(new CustomEvent('on-booking-engine-ready', {
            detail: 'Event System works as expected inside a Shadow Root.',
        }));
    }, []);
    return (_jsx("div", { className: "wrapper", children: _jsx(Title, { type: "h1", children: "Immersive Experience shadow root." }) }));
}, { shadow: 'open' });
defineGlobalFunctions();
const rootDiv = document.getElementById('root');
if (rootDiv) {
    const root = ReactDOM.createRoot(rootDiv);
    root.render(_jsx(WebComponentsTestPage, {}));
}
